import { Injectable, inject as inject_1 } from '@angular/core';

import { Logger, WindowRef } from '@frontend/vanilla/core';

import { PaymentsCommonConfig } from '../client-config/client-config.models';

/**
 * @whatItDoes Provides utility functions for injecting third-party-scripts.
 *
 * @description
 * # Overview
 * Used to inject third party scripts into our application. Call the inject() method with the key and wait for it to resolve before calling the script functionality.
 * The corresponding Dynacon configuration can be found under the name MobilePortal.CommonFeatures.ScriptsInjectionData.
 * The corresponding Scripts need to be placed within the scripts folder of our application.
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class ScriptInjectorService {
    private windowRef = inject_1(WindowRef);
    private logger = inject_1(Logger);
    private paymentsCommonConfig = inject_1(PaymentsCommonConfig);

    private nativeWindow: any;
    private document: Document;

    constructor() {
        this.nativeWindow = this.windowRef.nativeWindow as any;
        this.document = this.nativeWindow.document;
    }

    async inject(key: string, cacheBuster: string = ''): Promise<void> {
        if (this.document.getElementById(key + '-script')) return Promise.resolve();

        const scriptData = this.paymentsCommonConfig.scriptsInjectionData[key];
        if (!scriptData || !scriptData?.url) {
            this.logger.infoRemote(`Missing script config for key: ${key}`);
            return;
        }

        const originalUrl = scriptData?.url;
        const url = cacheBuster ? originalUrl + (originalUrl?.includes('?') ? '&' : '?') + cacheBuster : originalUrl;

        const script = this.document.createElement('script');
        script.setAttribute('src', url);
        this.setCustomAttributes(script, scriptData?.customAttributes);

        const promise = new Promise<void>((resolve, reject) => {
            script.onload = () => {
                this.logger.infoRemote(`${key} SDK loaded from CDN: ${url}`);
                resolve();
            };
            script.onerror = () => {
                const error = new Error(`Failed to load external script to populate with key` + key);
                reject(error);
                this.logger.errorRemote(`Failed to load external script to populate with key` + key, error);
            };
        });

        this.document.body.appendChild(script);
        return promise;
    }
    async StyleSheetInject(key: string, cacheBuster: string = ''): Promise<void> {
        if (this.document.getElementById(key + '-stylesheet')) return Promise.resolve();

        const data = this.paymentsCommonConfig.scriptsInjectionData[key];
        if (!data || !data?.styleSheetUrl) {
            this.logger.infoRemote(`Missing stylesheet config for key: ${key}`);
            return;
        }
        const originalUrl = data?.styleSheetUrl;
        const url = cacheBuster ? originalUrl + (originalUrl?.includes('?') ? '&' : '?') + cacheBuster : originalUrl;

        const stylesheet = this.document.createElement('link');
        if (url) {
            stylesheet.setAttribute('href', url);
        }
        this.setCustomAttributes(stylesheet, data?.customAttributes);
        const promise = new Promise<void>((resolve, reject) => {
            stylesheet.onload = () => {
                resolve();
            };
            stylesheet.onerror = () => {
                const error = new Error(`Failed to load external stylesheet to populate with key` + key);
                reject(error);
                this.logger.errorRemote(`Failed to load external stylesheet to populate with key` + key, error);
            };
        });

        this.document.head.appendChild(stylesheet);
        return promise;
    }
    /** Sets custom attributes */
    setCustomAttributes(element: HTMLElement, attributes?: Record<string, string | undefined>): void {
        if (!attributes) return;
        Object.entries(attributes).forEach(([key, value]) => {
            if (value !== undefined) element.setAttribute(key, value);
        });
    }
}
